import { useMemo } from 'react';
import { PathLayer, ScatterplotLayer } from '@deck.gl/layers';
import type { DTNetworkData } from './useDigitalTwinNetworkData';

const DT_PIPE_COLOR: [number, number, number, number] = [160, 61, 204, 255];
const DT_NODE_COLOR: [number, number, number, number] = [160, 61, 204, 180];

export function useDigitalTwinNetworkLayers({
  nodes,
  lines,
  enabled,
}: {
  nodes: DTNetworkData['nodes'];
  lines: DTNetworkData['lines'];
  enabled: boolean;
}): any[] {
  return useMemo(() => {
    if (!enabled || (!nodes.length && !lines.length)) return [];

    const layers: any[] = [];

    const validLines = lines.filter((d) => {
      const coords = d.geometry.type === 'LineString'
        ? d.geometry.coordinates
        : d.geometry.coordinates[0];
      return Array.isArray(coords) && coords.length >= 2;
    });

    if (validLines.length > 0) {
      layers.push(
        new PathLayer({
          id: 'dt-network-pipes',
          data: validLines,
          getPath: (d: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>) => {
            if (d.geometry.type === 'LineString') {
              return d.geometry.coordinates as [number, number][];
            }
            return d.geometry.coordinates[0] as [number, number][];
          },
          getColor: () => DT_PIPE_COLOR,
          widthMinPixels: 6,
          rounded: true,
          pickable: false,
        })
      );
    }

    if (nodes.length > 0) {
      layers.push(
        new ScatterplotLayer({
          id: 'dt-network-nodes',
          data: nodes,
          getPosition: (d: GeoJSON.Feature<GeoJSON.Point>) =>
            d.geometry.coordinates as [number, number],
          getFillColor: () => DT_NODE_COLOR,
          getRadius: 3,
          radiusMinPixels: 5,
          radiusMaxPixels: 8,
          pickable: false,
        })
      );
    }

    return layers;
  }, [nodes, lines, enabled]);
}

export default useDigitalTwinNetworkLayers;
